import { useCallback, useMemo, useState } from 'react';
import {
  getAdvancedSettingChanges,
  getHasAdvancedSettingsChanged,
  getValidationErrorMessages,
} from './utils';
import { ADVANCED } from './constants';

/**
 * Hook for Advanced Settings tab state, change detection, validation, and save/undo.
 *
 * @param {Object} options
 * @param {Object} options.advancedSettings - Current saved advanced settings (from property).
 * @param {Function} options.updateProperty - Called with (updates, tabData) after successful validation on save.
 * @returns {Object} Hook API
 * @returns {Object} return.advancedSettingsTabData - Editable copy of advanced settings (guest card + self-guided tours, etc.).
 * @returns {Function} return.clearFieldErrorMessage - Clears the stored message for a given field name.
 * @returns {Function} return.handleOnSave - Validates, then calls updateProperty with change payload and tab snapshot.
 * @returns {Function} return.handleOnUndo - Resets tab data to options.advancedSettings.
 * @returns {boolean} return.isActionsDisabled - True when there are no changes, or when a validation error is present.
 * @returns {Function} return.updateAdvancedSettingsSection - Curried updater for a tab section key.
 * @returns {Object} return.validationErrorMessages - Field-keyed validation messages (e.g. invalid self-guided tour URL).
 */
const useAdvancedSettings = ({ advancedSettings, updateProperty }) => {
  const [advancedSettingsTabData, setAdvancedSettingsTabData] =
    useState(advancedSettings);
  const [undoCount, setUndoCount] = useState(0);
  const [validationErrorMessages, setValidationErrorMessages] = useState({});

  const handleOnUndo = useCallback(() => {
    setAdvancedSettingsTabData(advancedSettings);
    setUndoCount((prevState) => prevState + 1);
  }, [advancedSettings]);

  const updateAdvancedSettingsSection = useCallback(
    (tabViewSection) => (updates) => {
      setAdvancedSettingsTabData((prev) => ({
        ...prev,
        [tabViewSection]: {
          ...prev[tabViewSection],
          ...updates,
        },
      }));
    },
    [],
  );

  const clearFieldErrorMessage = (field) => {
    setValidationErrorMessages((prevState) => ({
      ...prevState,
      [field]: '',
    }));
  };

  const validate = () => {
    const messages = getValidationErrorMessages(advancedSettingsTabData);
    if (messages) {
      setValidationErrorMessages(messages);
    }

    return !messages;
  };

  const handleOnSave = async () => {
    const isValid = validate();

    if (!isValid) {
      return;
    }

    const updates = getAdvancedSettingChanges(
      advancedSettings,
      advancedSettingsTabData,
    );
    const tabData = { [ADVANCED]: advancedSettingsTabData };
    updateProperty(updates, tabData);
  };

  const isActionsDisabled = useMemo(() => {
    const hasValidationErrors = Object.values(validationErrorMessages).some(
      Boolean,
    );

    return (
      hasValidationErrors ||
      !getHasAdvancedSettingsChanged(advancedSettings, advancedSettingsTabData)
    );
  }, [advancedSettings, advancedSettingsTabData, validationErrorMessages]);

  return {
    advancedSettingsTabData,
    clearFieldErrorMessage,
    handleOnSave,
    handleOnUndo,
    isActionsDisabled,
    undoCount,
    updateAdvancedSettingsSection,
    validationErrorMessages,
  };
};

export default useAdvancedSettings;
